import { useState } from 'react';
import firebase from 'firebase/compat/app';
import { auth, db } from '../../../services/firebase';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import type { Payment as PaymentRecord, PaymentMethod } from '../../../types/payment.types';

type PaymentProps = {
  setUser: React.Dispatch<React.SetStateAction<firebase.User | null>>;
  setShowPaymentComponent: React.Dispatch<React.SetStateAction<boolean>>;
  setPaymentCompleted: React.Dispatch<React.SetStateAction<boolean>>;
  address: string | null;
};

/**
 * PaymentForm component
 * Renders a payment form and handles payment processing using Stripe.
 *
 * @param setUser - User state setter (currently unused, marked for removal)
 * @param setShowPaymentComponent - Controls payment form visibility
 * @param setPaymentCompleted - Marks payment as completed after successful transaction
 * @param address - User's property address for the payment
 */
export const PaymentForm: React.FC<PaymentProps> = ({ setUser, setShowPaymentComponent, setPaymentCompleted, address }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const currentDate = new Date();
  const formattedDate = `${currentDate.getMonth() + 1}/${currentDate.getDate()}/${currentDate.getFullYear()}`;
  const newPaymentDate = `${currentDate.getMonth() + 2}/1/${currentDate.getFullYear()}`;

  /**
   * Generate unique payment ID
   */
  const generatePaymentId = (): string => {
    const timestamp = new Date().getTime();
    const random = Math.random().toString(36).substring(2, 8);
    return `pay_${timestamp}_${random}`;
  };

  /**
   * Calculate payment period and due date information
   */
  const calculatePaymentPeriod = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();

    // Period: current month (e.g., Oct 1 - Oct 31)
    const periodStart = new Date(year, month, 1);
    const periodEnd = new Date(year, month + 1, 0, 23, 59, 59, 999);

    // Due date: 1st of current month
    const dueDate = new Date(year, month, 1);

    return {
      periodStart: periodStart.toISOString(),
      periodEnd: periodEnd.toISOString(),
      dueDate: dueDate.toISOString(),
      paidDate: now.toISOString(),
      isLate: now > dueDate,
      daysLate: now > dueDate ? Math.floor((now.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)) : 0
    };
  };

  const finalizePayment = async () => {
    if (!stripe || !elements) {
      setError('Payment system not ready. Please refresh and try again.');
      return;
    }

    if (isProcessing) {
      return; // Prevent double submission
    }

    setIsProcessing(true);
    setError(null);

    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Confirm payment with Stripe
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          payment_method_data: {
            billing_details: {
              name: user.displayName || 'Tenant',
              email: user.email || '',
            },
          },
        },
        redirect: 'if_required',
      });

      if (result.error) {
        // Payment failed
        setError(result.error.message || 'Payment failed');
        setSuccess('Payment failed.');

        // Store failed payment attempt in Firebase
        const paymentId = generatePaymentId();
        const paymentPeriod = calculatePaymentPeriod();

        const failedPayment: Partial<PaymentRecord> = {
          paymentId,
          userId: user.uid,
          leaseId: 'lease_default', // TODO: Get from user data
          addressId: address || 'unknown',

          stripePaymentIntentId: result.error.payment_intent?.id || 'none',
          stripeCustomerId: null,

          amount: 0, // Amount not available in error
          currency: 'usd',
          status: 'failed',

          paymentMethod: {
            type: 'card',
            brand: null,
            last4: null,
            expiryMonth: null,
            expiryYear: null,
            fingerprint: null
          },

          billingDetails: {
            name: user.displayName || 'Tenant',
            email: user.email || ''
          },

          createdAt: new Date().toISOString(),
          processedAt: null,
          updatedAt: new Date().toISOString(),

          ...paymentPeriod,

          lateFee: paymentPeriod.isLate ? 5000 : 0,

          receiptUrl: null,
          receiptNumber: null,

          failureCode: result.error.code || null,
          failureMessage: result.error.message || 'Payment failed',

          refunded: false,
          refundedAt: null,
          refundAmount: 0,
          refundReason: null,

          description: `Rent payment for ${new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`,
          notes: null,
          tags: ['rent', 'failed']
        };

        await db.ref(`payments/${user.uid}/${paymentId}`).set(failedPayment);

      } else if (result.paymentIntent) {
        // Payment succeeded!
        setSuccess('Payment successful!');

        const paymentIntent = result.paymentIntent;
        const paymentId = generatePaymentId();
        const paymentPeriod = calculatePaymentPeriod();

        // Extract payment method details (safely)
        let paymentMethodDetails: PaymentMethod = {
          type: 'card',
          brand: null,
          last4: null,
          expiryMonth: null,
          expiryYear: null,
          fingerprint: null
        };

        // Extract payment method info from the payment intent
        if (paymentIntent.payment_method) {
          try {
            const pm = paymentIntent.payment_method;
            // Type guard: check if payment_method is expanded (object) vs just an ID (string)
            if (pm && typeof pm === 'object' && 'type' in pm) {
              const cardDetails = 'card' in pm ? pm.card : null;
              paymentMethodDetails = {
                type: pm.type as 'card' | 'ach' | 'other' || 'card',
                brand: cardDetails?.brand || null,
                last4: cardDetails?.last4 || null,
                expiryMonth: cardDetails?.exp_month || null,
                expiryYear: cardDetails?.exp_year || null,
                fingerprint: cardDetails?.fingerprint || null
              };
            }
          } catch (err) {
            // Payment method details extraction failed - use defaults
          }
        }

        // Extract customer ID safely
        let customerId: string | null = null;
        if ('customer' in paymentIntent && typeof paymentIntent.customer === 'string') {
          customerId = paymentIntent.customer;
        }

        // Extract receipt URL safely
        let receiptUrl: string | null = null;
        if ('charges' in paymentIntent && paymentIntent.charges && typeof paymentIntent.charges === 'object' && 'data' in paymentIntent.charges) {
          const chargesData = paymentIntent.charges.data as Array<{ receipt_url?: string }>;
          receiptUrl = chargesData[0]?.receipt_url || null;
        }

        // Create complete payment record
        const paymentRecord: Partial<PaymentRecord> = {
          paymentId,
          userId: user.uid,
          leaseId: 'lease_default', // TODO: Get from user data
          addressId: address || 'unknown',

          stripePaymentIntentId: paymentIntent.id,
          stripeCustomerId: customerId,

          amount: paymentIntent.amount,
          currency: paymentIntent.currency,
          status: paymentIntent.status as PaymentRecord['status'],

          paymentMethod: paymentMethodDetails,

          billingDetails: {
            name: user.displayName || 'Tenant',
            email: user.email || ''
          },

          createdAt: new Date(paymentIntent.created * 1000).toISOString(),
          processedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),

          ...paymentPeriod,

          lateFee: paymentPeriod.isLate ? 5000 : 0,

          receiptUrl,
          receiptNumber: paymentIntent.id.slice(-10),

          failureCode: null,
          failureMessage: null,

          refunded: false,
          refundedAt: null,
          refundAmount: 0,
          refundReason: null,

          description: `Rent payment for ${new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`,
          notes: null,
          tags: [
            'rent',
            paymentPeriod.isLate ? 'late' : 'on-time',
            paymentMethodDetails.type,
            paymentMethodDetails.brand || 'unknown'
          ]
        };

        // Store payment in Firebase
        await db.ref(`payments/${user.uid}/${paymentId}`).set(paymentRecord);

        // Update payment summary
        const summaryRef = db.ref(`paymentSummary/${user.uid}`);
        const summarySnapshot = await summaryRef.once('value');
        const currentSummary = summarySnapshot.val() || {
          userId: user.uid,
          totalPayments: 0,
          totalAmountPaid: 0,
          totalRefunded: 0,
          paymentHistory: { succeeded: 0, failed: 0, refunded: 0 }
        };

        await summaryRef.update({
          totalPayments: (currentSummary.totalPayments || 0) + 1,
          totalAmountPaid: (currentSummary.totalAmountPaid || 0) + paymentIntent.amount,
          lastPaymentDate: paymentRecord.paidDate,
          lastPaymentAmount: paymentIntent.amount,
          currentBalance: 0,
          isCurrentlyLate: false,
          'paymentHistory/succeeded': (currentSummary.paymentHistory?.succeeded || 0) + 1,
          updatedAt: new Date().toISOString()
        });

        // Update address rent paid status (backward compatibility)
        if (address) {
          await db.ref(`addresses/${address}`).update({
            rentPaid: true,
            paymentDate: formattedDate,
            dueDate: newPaymentDate,
            lastPaymentId: paymentId
          });
        }

        // Success!
        setPaymentCompleted(true);
        setTimeout(() => {
          setShowPaymentComponent(false);
        }, 2000);
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Payment failed';
      setError(errorMessage);
      setSuccess('Payment failed!');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <PaymentElement
        options={{
          wallets: {
            applePay: 'never',
            googlePay: 'never',
          },
        }}
      />
      <div className='c-button-div'>
        {error && <p className="error-message" style={{ color: '#ef4444', marginBottom: '1rem' }}>{error}</p>}
        {success && <p className="success-message" style={{ color: success.includes('failed') ? '#ef4444' : '#22c55e', marginBottom: '1rem' }}>{success}</p>}

        <button
          type="button"
          className='c-button confirm'
          onClick={finalizePayment}
          disabled={isProcessing || !stripe || !elements}
        >
          {isProcessing ? 'Processing...' : 'Confirm Payment'}
        </button>
        <button
          type="button"
          className='c-button cancel'
          onClick={() => setShowPaymentComponent(false)}
          disabled={isProcessing}
        >
          Cancel
        </button>
      </div>
    </>
  );
};

export default PaymentForm;
